import { LodPointCloud, LodTreeNode, PointCloudChunk } from "@faro-lotv/lotv";
import { Box3, Vector3 } from "three";
import { FloorSample } from "./floor-segmentation";

/** Points fetched for the floor segmentation, enough for 10 cm cells on large buildings */
const POINT_BUDGET = 3_000_000;

/** Nodes downloaded at the same time */
const CONCURRENCY = 8;

/** A Potree level has about four times the points of the one above it */
const LEVEL_GROWTH = 4;

/** One sample per point cloud, so moving the box only runs the segmentation again */
const samples = new WeakMap<LodPointCloud, Promise<FloorSample>>();

/**
 * The download is not aborted when the wizard closes, it is cached for the next time
 *
 * @param pointCloud The point cloud to sample
 * @returns the points of the point cloud levels that fit the budget, independent of the camera
 */
export function fetchFloorSample(
  pointCloud: LodPointCloud,
): Promise<FloorSample> {
  const cached = samples.get(pointCloud);
  if (cached) return cached;
  const sample = downloadSample(pointCloud);
  samples.set(pointCloud, sample);
  sample.catch(() => samples.delete(pointCloud));
  return sample;
}

/**
 * The sample holds whole LOD levels of the whole cloud, so its share in the region is the share of the full cloud
 *
 * @param totalBytes Size of the whole point cloud
 * @param sample The sample
 * @param pointsInRegion Number of sample points in the region and of the visible scans
 * @returns the export size of the region in bytes, undefined for an empty sample
 */
export function estimateRegionBytes(
  totalBytes: number,
  sample: FloorSample,
  pointsInRegion: number,
): number | undefined {
  const sampled = sample.positions.length / 3;
  if (!sampled) return;
  return Math.round((totalBytes * pointsInRegion) / sampled);
}

/**
 * @param pointCloud The point cloud to sample
 * @returns the sample, in world coordinates
 */
async function downloadSample(pointCloud: LodPointCloud): Promise<FloorSample> {
  const { tree } = pointCloud;
  const chunks: PointCloudChunk[] = [];
  let total = 0;
  let level: LodTreeNode[] = [tree.root];
  while (level.length) {
    const fetched = await mapWithConcurrency(level, async (node) =>
      (await tree.getNodePoints(node)).points(),
    );
    chunks.push(...fetched);
    const levelPoints = fetched.reduce(
      (sum, chunk) => sum + chunk.numPoints,
      0,
    );
    total += levelPoints;

    // The children are known once their parent hierarchy is loaded, their counts may not be
    const children = level.flatMap((node) => node.children);
    const known = children.reduce((sum, node) => sum + node.numPoints, 0);
    const next = known > 0 ? known : levelPoints * LEVEL_GROWTH;
    if (total + next > POINT_BUDGET) break;
    level = children;
  }

  pointCloud.updateWorldMatrix(true, false);
  const matrix = pointCloud.matrixWorld.elements;
  const origin = new Box3()
    .copy(tree.boundingBox)
    .applyMatrix4(pointCloud.matrixWorld)
    .getCenter(new Vector3());
  const positions = new Float32Array(total * 3);
  const hasScanIds = chunks.every((chunk) => chunk.scanIds);
  const scanIds = hasScanIds ? new Uint16Array(total) : undefined;
  let offset = 0;
  for (const chunk of chunks) {
    const src = chunk.positions;
    for (let i = 0; i < chunk.numPoints; i++) {
      const [x, y, z] = [src[i * 3], src[i * 3 + 1], src[i * 3 + 2]];
      // Relative to the origin, the world coordinates can be too large for a float32
      const o = (offset + i) * 3;
      positions[o] =
        matrix[0] * x + matrix[4] * y + matrix[8] * z + matrix[12] - origin.x;
      positions[o + 1] =
        matrix[1] * x + matrix[5] * y + matrix[9] * z + matrix[13] - origin.y;
      positions[o + 2] =
        matrix[2] * x + matrix[6] * y + matrix[10] * z + matrix[14] - origin.z;
    }
    if (scanIds && chunk.scanIds) {
      scanIds.set(chunk.scanIds.subarray(0, chunk.numPoints), offset);
    }
    offset += chunk.numPoints;
  }
  return { origin: origin.toArray(), positions, scanIds };
}

/**
 * @param items The items to process
 * @param task The async task to run on each item
 * @returns the results, in the order of the items
 */
async function mapWithConcurrency<T, R>(
  items: T[],
  task: (item: T) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  async function worker(): Promise<void> {
    while (next < items.length) {
      const index = next++;
      results[index] = await task(items[index]);
    }
  }
  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, items.length) }, worker),
  );
  return results;
}
